import React from 'react';
import { Card, CardContent, CardHeader, Typography, Box } from '@mui/material';

/** Max visual height (px) for the embedment zone above the foundation */
const EMBED_VISUAL_PX = 30;

/** Depth below ground (m, 0 at surface) → SVG y (downward), three-zone piecewise scale:
 *  1. 0..D_embed  → embedScale  (capped so above-foundation zone stays compact)
 *  2. D_embed..fullScaleDepth → pileZScale
 *  3. fullScaleDepth..∞ → compressedScale */
function depthBglToVisualY(depthM, groundLevel, D_embed, embedScale, fullScaleDepth, pileZScale, compressedScale) {
  const d = Math.max(0, Number(depthM) || 0);
  if (D_embed > 0.001 && d <= D_embed) {
    return groundLevel + d * embedScale;
  }
  const embedPx = D_embed > 0.001 ? D_embed * embedScale : 0;
  const base = groundLevel + embedPx;
  const belowEmbed = d - Math.max(0, D_embed);
  const fullScaleRange = Math.max(0, fullScaleDepth - Math.max(0, D_embed));
  if (belowEmbed <= fullScaleRange) {
    return base + belowEmbed * pileZScale;
  }
  return base + fullScaleRange * pileZScale + (belowEmbed - fullScaleRange) * compressedScale;
}

/** Depth b.g.l. of the first bedrock surface, or null when the profile has no rock. */
function bedrockSurfaceDepthM(soilLayers) {
  let depth = 0;
  for (const layer of soilLayers || []) {
    const name = String(layer?.name || layer?.soil_type || '').toLowerCase();
    if (name.includes('bedrock') || name.includes('rock')) return depth;
    depth += Number(layer?.thickness) || 0;
  }
  return null;
}

/** Pile toe depth. A bedrock surface caps the length: the toe bears on the rock and does not enter it. */
function cappedPileTipDepth(capBottomDepth, memberLength, rockSurfaceDepth) {
  const raw = capBottomDepth + Math.max(0, Number(memberLength) || 0);
  if (rockSurfaceDepth == null || !Number.isFinite(rockSurfaceDepth)) return raw;
  return Math.min(raw, Math.max(rockSurfaceDepth, capBottomDepth));
}

/** P-y samples → one polyline along the pile + arrows (y from depthY(D_embed+H+depth_along_pile)). */
function buildPileLateralGraphic({
  lateral_pressure_dist,
  pileLength,
  pileBottomVisual,
  D_embed,
  H,
  depthY,
  pileEdgeX,
  isLoadNegative,
  pressureScaleLat,
  keyPrefix = '',
  markerGreenUrl,
}) {
  const rows = lateral_pressure_dist
    .map((point, i) => ({
      i,
      depth: Math.max(0, point.depth || 0),
      pk_raw: point.pressure_kPa || 0,
    }))
    .filter((r) => r.depth <= pileLength + 1e-6)
    .sort((a, b) => a.depth - b.depth);

  const toPt = (r) => {
    const bgl = D_embed + H + r.depth;
    const y = depthY(bgl);
    if (y > pileBottomVisual + 2) return null;
    
    // pk_raw naturally contains the sign. 
    // If load is positive, p0 > 0. pileEdgeX is on the right.
    // If load is negative, p0 < 0. pileEdgeX is on the left.
    // By adding (pk_raw * scale), the point naturally shifts left or right.
    const signedLen = r.pk_raw * pressureScaleLat;
    const arrowLen = Math.abs(signedLen);
    const tipX = pileEdgeX + signedLen;
    
    return { ...r, y, tipX, arrowLen, signedLen };
  };

  const pts = rows.map(toPt).filter(Boolean);
  let fullStr = pts.map((p) => `${p.tipX},${p.y}`).join(' ');
  if (pts.length === 1) {
    const p = pts[0];
    fullStr = `${pileEdgeX},${p.y} ${p.tipX},${p.y}`;
  }

  const arrows = [];
  pts.forEach((p, j) => {
    // Draw arrows more frequently to better capture the nonlinear shape
    if (j % 3 === 0 && p.arrowLen > 0.1) {
      // Determine direction of arrow based on actual side it is drawn on
      const isDrawingLeft = p.tipX < pileEdgeX;
      arrows.push(
        <line
          key={`${keyPrefix}-${p.i}-${j}`}
          x1={p.tipX}
          y1={p.y}
          x2={pileEdgeX}
          y2={p.y}
          stroke="#4caf50"
          strokeWidth="1"
          markerEnd={markerGreenUrl}
        />
      );
    }
  });

  return { fullStr, arrows, hasLine: pts.length >= 1 };
}

const SoilPressureDiagram = ({ results }) => {
  const { foundation_geometry, equilibrium, stability_verifications, input_parameters } = results;

  if (!foundation_geometry || !stability_verifications || !stability_verifications.bearing) {
    return (
      <Card>
        <CardHeader title="Soil Pressure Distribution" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Pressure distribution data not available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const { dimensions, pile_layout, embedment_depth } = foundation_geometry;
  const D_embed = embedment_depth || 0;
  const bearing = stability_verifications.bearing;
  const loads = input_parameters?.loads || {};
  const soilLayers = input_parameters.soil_layers || [];
  const slabLatObj = results.slab_lateral_pressure_distribution || {};
  const groundwaterDepthM = (() => {
    const g = input_parameters?.groundwater_depth ?? slabLatObj.groundwater_depth_m;
    if (g === null || g === undefined || g === '') return null;
    const n = Number(g);
    return n > 0 && Number.isFinite(n) ? n : null;
  })();

  const horizontal_load_x = Number(loads.Vx) || 0;
  const horizontal_load_y = Number(loads.Vy) || 0;

  const L = dimensions.L || 2.0; 
  const W = dimensions.W || 2.0; 
  const H = dimensions.H || 0.8; 

  const max_pressure_xz = bearing.max_pressure_xz ?? bearing.max_pressure ?? 0;
  const min_pressure_xz = bearing.min_pressure_xz ?? bearing.min_pressure ?? 0;
  const max_pressure_yz = bearing.max_pressure_yz ?? bearing.max_pressure ?? 0;
  const min_pressure_yz = bearing.min_pressure_yz ?? bearing.min_pressure ?? 0;

  const lateral_pressure_dist_obj = results.lateral_pressure_distribution || { distribution: [], tip_boundary_condition: {} };
  const lateral_pressure_dist = lateral_pressure_dist_obj.distribution || [];
  const tip_condition = lateral_pressure_dist_obj.tip_boundary_condition || {};
  
  const slab_lateral_dist = slabLatObj.distribution || [];
  const slab_bearing_dist = results.equilibrium?.slab_bearing_pressure_distribution || [];
  const pileLength = results.input_parameters?.actual_pile_length || results.input_parameters?.pile_length || 12.0;
  const originalPileLength = results.input_parameters?.pile_length || 12.0;

  const pile_loads = equilibrium?.pile_loads || [];
  const pile_lateral_x = equilibrium?.pile_lateral_x || [];
  const pile_lateral_y = equilibrium?.pile_lateral_y || [];
  
  const pilesByX = {}; 
  const pilesByY = {}; 

  pile_layout.forEach((pile, index) => {
    let x_pos = Array.isArray(pile) ? pile[0] : 0;
    let y_pos = Array.isArray(pile) ? pile[1] : 0;
    let angle_x = Array.isArray(pile) && pile.length >= 5 ? pile[2] : 0;
    let angle_y = Array.isArray(pile) && pile.length >= 5 ? pile[3] : 0;
    let delta_z = Array.isArray(pile) && pile.length >= 5 ? pile[4] : (pile.length >= 4 ? pile[3] : (pile.length > 2 ? pile[2] : pileLength));

    const x_key = x_pos.toFixed(3);
    if (!pilesByX[x_key]) pilesByX[x_key] = [];
    pilesByX[x_key].push({ 
      index, x_pos, y_pos, angle_x, angle_y, delta_z, 
      load: pile_loads[index] || 0,
      lateral_x: pile_lateral_x[index] || 0,
      lateral_y: pile_lateral_y[index] || 0
    });

    const y_key = y_pos.toFixed(3);
    if (!pilesByY[y_key]) pilesByY[y_key] = [];
    pilesByY[y_key].push({ 
      index, x_pos, y_pos, angle_x, angle_y, delta_z, 
      load: pile_loads[index] || 0,
      lateral_x: pile_lateral_x[index] || 0,
      lateral_y: pile_lateral_y[index] || 0
    });
  });

  const svgWidth = 700;
  const svgHeight = 850; // EVEN TALLER to prevent clipping
  /** Cap length (XZ) / width (YZ) drawn at this fraction of svg width — reference for the section. */
  const CAP_WIDTH_FRACTION = 0.7;
  /** Uniform vertical scale from ground down to this depth (m b.g.l.): slab bottom + this margin below slab. */
  const CRITICAL_DEPTH_BELOW_SLAB_BOTTOM_M = 2.0;

  const renderXZView = () => {
    const capPx = CAP_WIDTH_FRACTION * svgWidth;
    const offsetX = (svgWidth - capPx) / 2;
    const offsetZ = 72;

    const soilProfileDepth = soilLayers.reduce((sum, layer) => sum + (layer.thickness || 0), 0);
    const fullScaleDepth = D_embed + H + CRITICAL_DEPTH_BELOW_SLAB_BOTTOM_M;
    const sectionScale = capPx / L;
    const pileZScale = sectionScale;
    const rockSurfaceDepth = bedrockSurfaceDepthM(soilLayers);
    const profileDepthMax = Math.max(
      soilProfileDepth,
      cappedPileTipDepth(D_embed + H, originalPileLength, rockSurfaceDepth),
      0.1,
    );
    const tailDepthM = Math.max(0, profileDepthMax - fullScaleDepth);
    const TAIL_BUDGET_PX = 480;
    const compressedScale =
      tailDepthM > 0.01 ? Math.max(4, Math.min(28, TAIL_BUDGET_PX / tailDepthM)) : pileZScale;
    const groundLevel = offsetZ;
    const embedScale = D_embed > 0.001 ? Math.min(pileZScale, EMBED_VISUAL_PX / D_embed) : pileZScale;
    const depthY = (d) => depthBglToVisualY(d, groundLevel, D_embed, embedScale, fullScaleDepth, pileZScale, compressedScale);
    const yGeolTop = depthY(D_embed);
    const yGeolBot = depthY(D_embed + H);
    const thickGeo = Math.max(1, yGeolBot - yGeolTop);
    const foundationTop = yGeolTop;
    const foundationBottom = yGeolBot;
    const foundationVisualHeight = thickGeo;

    const contentYBottom = depthY(profileDepthMax) + 140;
    const viewYTop = groundLevel - 28;
    const viewBoxHeight = Math.max(svgHeight, contentYBottom - viewYTop);

    const dxB = L / 8;
    const dyB = W / 8;
    const xGroupsB = {};
    slab_bearing_dist.forEach((p) => {
      const xKey = p.x.toFixed(3);
      if (!xGroupsB[xKey]) xGroupsB[xKey] = { x: p.x, sumP: 0, count: 0 };
      xGroupsB[xKey].sumP += p.pressure_kPa;
      xGroupsB[xKey].count += 1;
    });
    const sortedXB = Object.values(xGroupsB).sort((a, b) => a.x - b.x);
    let w_min_label = sortedXB.length ? (sortedXB[0].sumP * dyB).toFixed(1) : (min_pressure_xz * W).toFixed(1);
    let w_max_label = sortedXB.length
      ? (sortedXB[sortedXB.length - 1].sumP * dyB).toFixed(1)
      : (max_pressure_xz * W).toFixed(1);

    return (
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 ${viewYTop} ${svgWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMin meet"
        style={{ border: '1px solid #eee', marginBottom: 20, backgroundColor: '#fdfdfd' }}
      >
        <defs>
          <marker id="arrowheadRedXZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#f44336" />
          </marker>
          <marker id="arrowheadBlueXZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#2196f3" />
          </marker>
          <marker id="arrowheadGreenXZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#4caf50" />
          </marker>
          <marker id="arrowheadOrangeXZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#ff6b35" />
          </marker>
        </defs>

        {/* --- SOIL LAYERS BACKGROUND --- */}
        {(() => {
          let currentDepth = 0;
          return soilLayers.map((layer, idx) => {
            const thickness = layer.thickness || 5.0;
            const layerTopY = depthY(currentDepth);
            const layerBottomY = depthY(currentDepth + thickness);
            const visualThickness = layerBottomY - layerTopY;
            const isBedrock = layer.name.toLowerCase().includes('bedrock') || layer.name.toLowerCase().includes('rock');
            currentDepth += thickness;

            return (
              <g key={`layer-xz-${idx}`}>
                <rect
                  x={50} y={layerTopY}
                  width={svgWidth - 100} height={visualThickness}
                  fill={isBedrock ? "#d7ccc8" : "#efebe9"}
                  opacity={isBedrock ? 0.8 : 0.3}
                />
                <line
                  x1={50} y1={layerBottomY}
                  x2={svgWidth - 50} y2={layerBottomY}
                  stroke="#8d6e63" strokeWidth="0.5" strokeDasharray="2 2"
                />
                <text x={60} y={layerTopY + 15} fontSize="10" fill="#8d6e63" fontWeight="bold">
                  {layer.name} ({thickness}m)
                </text>
              </g>
            );
          });
        })()}

        {/* --- GROUND LEVEL --- */}
        <line x1={50} y1={groundLevel} x2={svgWidth - 50} y2={groundLevel} stroke="#8d6e63" strokeWidth="2" strokeDasharray="5 5" />
        <text x={svgWidth - 60} y={groundLevel - 10} textAnchor="end" fontSize="10" fill="#8d6e63" fontWeight="bold">Ground Surface</text>

        {groundwaterDepthM != null && (() => {
          const yGwl = depthY(groundwaterDepthM);
          return (
            <g>
              <line
                x1={50}
                y1={yGwl}
                x2={svgWidth - 50}
                y2={yGwl}
                stroke="#0288d1"
                strokeWidth="2"
                strokeDasharray="8 5"
                opacity={0.95}
              />
              <text x={svgWidth - 58} y={yGwl - 6} textAnchor="end" fontSize="10" fill="#0288d1" fontWeight="bold">
                Ground water level ({groundwaterDepthM.toFixed(2)} m bgl)
              </text>
            </g>
          );
        })()}

        {/* --- SOIL PRESSURE DISTRIBUTION (Bearing) --- */}
        {(() => {
          const arrows = [];
          const points = [];
          
          // Group points by X coordinate to calculate total line load per slice (kN/m)
          const xGroups = {};
          slab_bearing_dist.forEach(p => {
            const xKey = p.x.toFixed(3);
            if (!xGroups[xKey]) xGroups[xKey] = { x: p.x, sumP: 0, count: 0 };
            xGroups[xKey].sumP += p.pressure_kPa;
            xGroups[xKey].count += 1;
          });

          const sortedX = Object.values(xGroups).sort((a, b) => a.x - b.x);
          const dx = L / 8;
          const dy = W / 8;

          if (sortedX.length === 0) return null;

          // Dynamic scaling based on line loads
          const max_line_load = Math.max(...sortedX.map(g => g.sumP * dy), 1);
          const dynamic_scale = 60 / max_line_load;

          sortedX.forEach((group, i) => {
            const x_start = offsetX + (L/2 + group.x - dx/2) * sectionScale;
            const x_mid = offsetX + (L/2 + group.x) * sectionScale;
            const x_end = offsetX + (L/2 + group.x + dx/2) * sectionScale;
            
            // w [kN/m] = sum(P [kPa]) * dy [m]
            const line_load = group.sumP * dy;
            const arrowLen = line_load * dynamic_scale;
            
            // Draw piece-wise step (staircase)
            if (i === 0) {
              points.push(`${x_start},${foundationBottom}`);
              points.push(`${x_start},${foundationBottom + arrowLen}`);
            } else {
              // Vertical step from previous element level
              const prev_line_load = sortedX[i-1].sumP * dy;
              const prev_arrowLen = prev_line_load * dynamic_scale;
              points.push(`${x_start},${foundationBottom + prev_arrowLen}`);
              points.push(`${x_start},${foundationBottom + arrowLen}`);
            }
            
            points.push(`${x_end},${foundationBottom + arrowLen}`);
            
            if (i === sortedX.length - 1) {
              points.push(`${x_end},${foundationBottom}`);
            }

            // Add arrow at center of element
            if (arrowLen > 2) {
              arrows.push(
                <line key={i} x1={x_mid} y1={foundationBottom + arrowLen} x2={x_mid} y2={foundationBottom} stroke="#2196f3" strokeWidth="1.5" opacity="0.6" markerEnd="url(#arrowheadBlueXZ)" />
              );
            }
          });

          return (
            <g>
              <polyline points={points.join(' ')} stroke="#2196f3" strokeWidth="2" fill="none" strokeDasharray="4 2" />
              {arrows}
            </g>
          );
        })()}
        <text x={offsetX} y={foundationBottom + 75} textAnchor="middle" fontSize="10" fill="#2196f3" fontWeight="bold">
          {w_min_label} kN/m
        </text>
        <text x={offsetX + L * sectionScale} y={foundationBottom + 75} textAnchor="middle" fontSize="10" fill="#2196f3" fontWeight="bold">
          {w_max_label} kN/m
        </text>

        {/* --- FOUNDATION BLOCK --- */}
        <rect x={offsetX} y={foundationTop} width={L * sectionScale} height={foundationVisualHeight} fill="#e0e0e0" stroke="#333" strokeWidth="2" />

        {/* --- SLAB LATERAL PRESSURE --- */}
        {slab_lateral_dist.length > 0 && Math.abs(horizontal_load_x) > 0.001 && (() => {
          const isNegative = horizontal_load_x < 0;
          const x_face = isNegative ? offsetX : offsetX + L * sectionScale;
          const maxPressure = Math.max(...slab_lateral_dist.map(p => Math.abs(p.pressure_kPa || 0)), 1);
          const slabScale = 60 / maxPressure;
          const points = [];
          const arrows = [];
          
          slab_lateral_dist.forEach((point, i) => {
            const depth = point.depth || 0;
            const visualDepth = depthY(D_embed + Math.min(depth, H));
            const arrowLen = Math.abs(point.pressure_kPa) * slabScale;
            const tipX = isNegative ? x_face - arrowLen : x_face + arrowLen;
            points.push(`${tipX},${visualDepth}`);
            if (i % 5 === 0 && arrowLen > 2) {
              arrows.push(<line key={i} x1={tipX} y1={visualDepth} x2={x_face} y2={visualDepth} stroke="#4caf50" strokeWidth="1.5" markerEnd="url(#arrowheadGreenXZ)" />);
            }
          });
          return (
            <g>
              <polyline points={points.join(' ')} fill="none" stroke="#4caf50" strokeWidth="2" strokeDasharray="4 2" />
              {arrows}
              <text
                x={isNegative ? x_face - 10 : x_face + 10}
                y={foundationBottom + 12}
                dominantBaseline="hanging"
                textAnchor={isNegative ? "end" : "start"}
                fontSize="10"
                fill="#4caf50"
                fontWeight="bold"
              >
                Passive (Rankine){slabLatObj.mobilisation_factor != null && slabLatObj.mobilisation_factor < 0.999 ? ` m=${slabLatObj.mobilisation_factor}` : ''}
              </text>
            </g>
          );
        })()}

        {/* --- PILES --- */}
        {Object.entries(pilesByX).map(([x_key, piles]) => {
          const x_val = parseFloat(x_key);
          const x_visual = offsetX + (L / 2 + x_val) * sectionScale;
          const totalLoad = piles.reduce((sum, p) => sum + (p.load || 0), 0);

          // Get pile properties from the first pile at this X location
          const pileAngleX = piles[0]?.angle_x || 0;
          const pileAngleY = piles[0]?.angle_y || 0;
          const pileDeltaZ = piles[0]?.delta_z ?? pileLength;
          const tipDepth = cappedPileTipDepth(D_embed + H, pileDeltaZ, rockSurfaceDepth);
          const drawnMemberLength = Math.max(0, tipDepth - (D_embed + H));
          const pileBottomVisual = depthY(tipDepth);

          const isDiagonal = pileAngleX !== 0 || pileAngleY !== 0;
          // Calculate the effective angle for visualization
          const currentAngle = Math.sqrt(pileAngleX ** 2 + pileAngleY ** 2);
          // Projected vector for visualization (show actual projection)
          const projectedLengthX = isDiagonal ? pileDeltaZ * Math.tan(pileAngleX * Math.PI / 180) : 0;
          const projectedVisualX = x_visual + projectedLengthX * 0.3; // Scale for visibility

          return (
            <g key={`pile-${x_key}`}>
              {/* Actual Pile - from foundation bottom to pile tip */}
              {currentAngle === 0 ? (
                // Vertical pile - draw as rectangle
                <rect x={x_visual - 10} y={foundationBottom} width={20} height={pileBottomVisual - foundationBottom} fill="#aaa" opacity={0.7} stroke="#666" strokeWidth="1" />
              ) : (
                // Diagonal pile - draw as angled line
                <line
                  x1={x_visual}
                  y1={foundationBottom}
                  x2={x_visual + Math.tan(pileAngleX * Math.PI / 180) * (pileBottomVisual - foundationBottom)}
                  y2={pileBottomVisual}
                  stroke="#666"
                  strokeWidth="3"
                  opacity={0.7}
                />
              )}

              {/* Projected vector indicator for diagonal piles */}
              {isDiagonal && (
                <>
                  <line
                    x1={x_visual}
                    y1={foundationBottom + 5}
                    x2={projectedVisualX}
                    y2={foundationBottom + 5}
                    stroke="#ff6b35"
                    strokeWidth="2"
                    strokeDasharray="5 3"
                    opacity="0.8"
                    markerEnd="url(#arrowheadOrangeXZ)"
                  />
                </>
              )}

              {/* Tick: fullScaleDepth m b.g.l. where vertical scale switches (only if pile crosses below that depth) */}
              {originalPileLength === pileLength &&
                D_embed + H + pileLength > fullScaleDepth + 0.02 &&
                pileBottomVisual >= depthY(fullScaleDepth) - 2 && (
                <line
                  x1={x_visual - 15} y1={depthY(fullScaleDepth)}
                  x2={x_visual + 15} y2={depthY(fullScaleDepth)}
                  stroke="#ff6b6b" strokeWidth="1" strokeDasharray="2 2"
                >
                  <title>
                    {fullScaleDepth.toFixed(2)} m b.g.l. (slab bottom + {CRITICAL_DEPTH_BELOW_SLAB_BOTTOM_M} m): denser vertical
                    scale below. Not a soil layer boundary.
                  </title>
                </line>
              )}

              {lateral_pressure_dist.length > 0 && Math.abs(horizontal_load_x) > 0.001 && (() => {
                const maxPressure = Math.max(...lateral_pressure_dist.map((p) => Math.abs(p.pressure_kPa || 0)), 1);
                const pressureScaleLat = 60 / maxPressure;
                const p0 = lateral_pressure_dist[0]?.pressure_kPa;
                const isNegative =
                  p0 !== undefined && p0 !== 0 && Math.abs(p0) > 1e-9
                    ? p0 < 0
                    : horizontal_load_x < 0 || (horizontal_load_x === 0 && horizontal_load_y < 0);
                const pileEdgeX = x_visual + (isNegative ? -10 : 10);
                const { fullStr, arrows, hasLine } = buildPileLateralGraphic({
                  lateral_pressure_dist,
                  pileLength: drawnMemberLength,
                  pileBottomVisual,
                  D_embed,
                  H,
                  depthY,
                  pileEdgeX,
                  isLoadNegative: isNegative,
                  pressureScaleLat,
                  keyPrefix: `xz-${x_key}-`,
                  markerGreenUrl: 'url(#arrowheadGreenXZ)',
                });
                return (
                  <g>
                    {hasLine && (
                      <polyline
                        points={fullStr}
                        fill="none"
                        stroke="#4caf50"
                        strokeWidth="2"
                        strokeDasharray="4 2"
                      >
                        <title>Lateral p–y reaction (shape vs depth along pile)</title>
                      </polyline>
                    )}
                    {arrows}
                  </g>
                );
              })()}

              {/* Tip reaction if pinned and in bedrock */}
              {tip_condition.type === 'pinned' && tip_condition.is_bedrock && (
                <g>
                  <path d={`M ${x_visual - 15} ${pileBottomVisual} L ${x_visual + 15} ${pileBottomVisual} L ${x_visual} ${pileBottomVisual - 10} Z`} fill="#8d6e63" />
                  {Math.abs(horizontal_load_x) > 1 && (
                    <line x1={x_visual + (horizontal_load_x > 0 ? 40 : -40)} y1={pileBottomVisual} x2={x_visual} y2={pileBottomVisual} stroke="#4caf50" strokeWidth="3" markerEnd="url(#arrowheadGreenXZ)" />
                  )}
                </g>
              )}

              <line x1={x_visual} y1={foundationBottom - 30} x2={x_visual} y2={foundationBottom} stroke="#f44336" strokeWidth="2" markerEnd="url(#arrowheadRedXZ)" />
              <rect x={x_visual - 35} y={foundationBottom - 50} width={70} height={12} fill="white" stroke="none" opacity={0.8} />
              <text x={x_visual} y={foundationBottom - 40} textAnchor="middle" fontSize="10" fill="#f44336" fontWeight="bold">
                {piles.length === 1 ? `${totalLoad.toFixed(0)}kN` : piles.map(p => `${p.load.toFixed(0)}`).join('|') + 'kN'}
              </text>

              {/* Lateral load labels for piles (XZ View - show X lateral load) */}
              {(() => {
                const totalLatX = piles.reduce((sum, p) => sum + (p.lateral_x || 0), 0);
                if (Math.abs(totalLatX) < 1.0) return null; // Only show if >1kN
                const isNegative = totalLatX < 0;
                const arrowXStart = x_visual;
                const arrowXEnd = isNegative ? x_visual + 40 : x_visual - 40;
                return (
                  <g>
                    <line x1={arrowXStart} y1={foundationBottom + 20} x2={arrowXEnd} y2={foundationBottom + 20} stroke="#2196f3" strokeWidth="1.5" markerEnd="url(#arrowheadBlueXZ)" />
                    <rect x={x_visual - 35} y={foundationBottom + 25} width={70} height={12} fill="white" stroke="none" opacity={0.8} />
                    <text x={x_visual} y={foundationBottom + 35} textAnchor="middle" fontSize="9" fill="#2196f3" fontWeight="bold">
                      {piles.length === 1 ? `${Math.abs(totalLatX).toFixed(1)}kN` : piles.map(p => `${Math.abs(p.lateral_x).toFixed(1)}`).join('|') + 'kN'}
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        <text x={offsetX + L * sectionScale / 2} y={contentYBottom - 24} textAnchor="middle" fontSize="14" fill="#666" fontWeight="bold">XZ View (Looking along Y-axis)</text>
      </svg>
    );
  };

  const renderYZView = () => {
    const capPx = CAP_WIDTH_FRACTION * svgWidth;
    const offsetX = (svgWidth - capPx) / 2;
    const offsetZ = 72;

    const soilProfileDepth = soilLayers.reduce((sum, layer) => sum + (layer.thickness || 0), 0);
    const fullScaleDepth = D_embed + H + CRITICAL_DEPTH_BELOW_SLAB_BOTTOM_M;
    const sectionScale = capPx / W;
    const pileZScale = sectionScale;
    const rockSurfaceDepth = bedrockSurfaceDepthM(soilLayers);
    const profileDepthMax = Math.max(
      soilProfileDepth,
      cappedPileTipDepth(D_embed + H, originalPileLength, rockSurfaceDepth),
      0.1,
    );
    const tailDepthM = Math.max(0, profileDepthMax - fullScaleDepth);
    const TAIL_BUDGET_PX = 480;
    const compressedScale =
      tailDepthM > 0.01 ? Math.max(4, Math.min(28, TAIL_BUDGET_PX / tailDepthM)) : pileZScale;
    const groundLevel = offsetZ;
    const embedScale = D_embed > 0.001 ? Math.min(pileZScale, EMBED_VISUAL_PX / D_embed) : pileZScale;
    const depthY = (d) => depthBglToVisualY(d, groundLevel, D_embed, embedScale, fullScaleDepth, pileZScale, compressedScale);
    const yGeolTop = depthY(D_embed);
    const yGeolBot = depthY(D_embed + H);
    const thickGeo = Math.max(1, yGeolBot - yGeolTop);
    const foundationTop = yGeolTop;
    const foundationBottom = yGeolBot;
    const foundationVisualHeight = thickGeo;

    const contentYBottom = depthY(profileDepthMax) + 140;
    const viewYTop = groundLevel - 28;
    const viewBoxHeight = Math.max(svgHeight, contentYBottom - viewYTop);

    const dxByz = L / 8;
    const dyByz = W / 8;
    const yGroupsB = {};
    slab_bearing_dist.forEach((p) => {
      const yKey = p.y.toFixed(3);
      if (!yGroupsB[yKey]) yGroupsB[yKey] = { y: p.y, sumP: 0, count: 0 };
      yGroupsB[yKey].sumP += p.pressure_kPa;
      yGroupsB[yKey].count += 1;
    });
    const sortedYB = Object.values(yGroupsB).sort((a, b) => a.y - b.y);
    let w_min_label_yz = sortedYB.length ? (sortedYB[0].sumP * dxByz).toFixed(1) : (min_pressure_yz * L).toFixed(1);
    let w_max_label_yz = sortedYB.length
      ? (sortedYB[sortedYB.length - 1].sumP * dxByz).toFixed(1)
      : (max_pressure_yz * L).toFixed(1);

    return (
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 ${viewYTop} ${svgWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMin meet"
        style={{ border: '1px solid #eee', marginBottom: 20, backgroundColor: '#fdfdfd' }}
      >
        <defs>
          <marker id="arrowheadRedYZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#f44336" />
          </marker>
          <marker id="arrowheadBlueYZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#2196f3" />
          </marker>
          <marker id="arrowheadGreenYZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#4caf50" />
          </marker>
          <marker id="arrowheadOrangeYZ" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#ff6b35" />
          </marker>
        </defs>
        {/* --- SOIL LAYERS BACKGROUND --- */}
        {(() => {
          let currentDepth = 0;
          return soilLayers.map((layer, idx) => {
            const thickness = layer.thickness || 5.0;
            const layerTopY = depthY(currentDepth);
            const layerBottomY = depthY(currentDepth + thickness);
            const visualThickness = layerBottomY - layerTopY;
            const isBedrock = layer.name.toLowerCase().includes('bedrock') || layer.name.toLowerCase().includes('rock');
            currentDepth += thickness;

            return (
              <g key={`layer-yz-${idx}`}>
                <rect x={50} y={layerTopY} width={svgWidth - 100} height={visualThickness} fill={isBedrock ? "#d7ccc8" : "#efebe9"} opacity={isBedrock ? 0.8 : 0.3} />
                <line
                  x1={50} y1={layerBottomY}
                  x2={svgWidth - 50} y2={layerBottomY}
                  stroke="#8d6e63" strokeWidth="0.5" strokeDasharray="2 2"
                />
                <text x={60} y={layerTopY + 15} fontSize="10" fill="#8d6e63" fontWeight="bold">{layer.name} ({thickness}m)</text>
              </g>
            );
          });
        })()}

        {/* --- GROUND LEVEL --- */}
        <line x1={50} y1={groundLevel} x2={svgWidth - 50} y2={groundLevel} stroke="#8d6e63" strokeWidth="2" strokeDasharray="5 5" />
        <text x={svgWidth - 60} y={groundLevel - 10} textAnchor="end" fontSize="10" fill="#8d6e63" fontWeight="bold">Ground Surface</text>

        {groundwaterDepthM != null && (() => {
          const yGwl = depthY(groundwaterDepthM);
          return (
            <g>
              <line
                x1={50}
                y1={yGwl}
                x2={svgWidth - 50}
                y2={yGwl}
                stroke="#0288d1"
                strokeWidth="2"
                strokeDasharray="8 5"
                opacity={0.95}
              />
              <text x={svgWidth - 58} y={yGwl - 6} textAnchor="end" fontSize="10" fill="#0288d1" fontWeight="bold">
                Ground water level ({groundwaterDepthM.toFixed(2)} m bgl)
              </text>
            </g>
          );
        })()}

        {/* --- SOIL PRESSURE DISTRIBUTION (Bearing) --- */}
        {(() => {
          const arrows = [];
          const points = [];
          
          // Group points by Y coordinate to calculate total line load per slice (kN/m)
          const yGroups = {};
          slab_bearing_dist.forEach(p => {
            const yKey = p.y.toFixed(3);
            if (!yGroups[yKey]) yGroups[yKey] = { y: p.y, sumP: 0, count: 0 };
            yGroups[yKey].sumP += p.pressure_kPa;
            yGroups[yKey].count += 1;
          });

          const sortedY = Object.values(yGroups).sort((a, b) => a.y - b.y);
          const dy = W / 8;
          const dx = L / 8;

          if (sortedY.length === 0) return null;

          const max_line_load = Math.max(...sortedY.map(g => g.sumP * dx), 1);
          const dynamic_scale = 60 / max_line_load;

          sortedY.forEach((group, i) => {
            const y_start = offsetX + (W/2 + group.y - dy/2) * sectionScale;
            const y_mid = offsetX + (W/2 + group.y) * sectionScale;
            const y_end = offsetX + (W/2 + group.y + dy/2) * sectionScale;
            
            // w [kN/m] = sum(P [kPa]) * dx [m]
            const line_load = group.sumP * dx;
            const arrowLen = line_load * dynamic_scale;
            
            // Draw piece-wise step (staircase)
            if (i === 0) {
              points.push(`${y_start},${foundationBottom}`);
              points.push(`${y_start},${foundationBottom + arrowLen}`);
            } else {
              const prev_line_load = sortedY[i-1].sumP * dx;
              const prev_arrowLen = prev_line_load * dynamic_scale;
              points.push(`${y_start},${foundationBottom + prev_arrowLen}`);
              points.push(`${y_start},${foundationBottom + arrowLen}`);
            }
            
            points.push(`${y_end},${foundationBottom + arrowLen}`);
            
            if (i === sortedY.length - 1) {
              points.push(`${y_end},${foundationBottom}`);
            }

            if (arrowLen > 2) {
              arrows.push(
                <line key={i} x1={y_mid} y1={foundationBottom + arrowLen} x2={y_mid} y2={foundationBottom} stroke="#2196f3" strokeWidth="1.5" opacity="0.6" markerEnd="url(#arrowheadBlueYZ)" />
              );
            }
          });

          return (
            <g>
              <polyline points={points.join(' ')} stroke="#2196f3" strokeWidth="2" fill="none" strokeDasharray="4 2" />
              {arrows}
            </g>
          );
        })()}
        <text x={offsetX} y={foundationBottom + 75} textAnchor="middle" fontSize="10" fill="#2196f3" fontWeight="bold">
          {w_min_label_yz} kN/m
        </text>
        <text x={offsetX + W * sectionScale} y={foundationBottom + 75} textAnchor="middle" fontSize="10" fill="#2196f3" fontWeight="bold">
          {w_max_label_yz} kN/m
        </text>

        {/* --- FOUNDATION BLOCK --- */}
        <rect x={offsetX} y={foundationTop} width={W * sectionScale} height={foundationVisualHeight} fill="#e0e0e0" stroke="#333" strokeWidth="2" />

        {/* --- SLAB LATERAL PRESSURE (YZ: face ∥ X, load Vy) --- */}
        {slab_lateral_dist.length > 0 && Math.abs(horizontal_load_y) > 0.001 && (() => {
          const isNegative = horizontal_load_y < 0;
          const x_face = isNegative ? offsetX : offsetX + W * sectionScale;
          const maxPressure = Math.max(...slab_lateral_dist.map((p) => Math.abs(p.pressure_kPa || 0)), 1);
          const slabScale = 60 / maxPressure;
          const points = [];
          const arrows = [];
          slab_lateral_dist.forEach((point, i) => {
            const depth = point.depth || 0;
            const visualDepth = depthY(D_embed + Math.min(depth, H));
            const arrowLen = Math.abs(point.pressure_kPa || 0) * slabScale;
            const tipX = isNegative ? x_face - arrowLen : x_face + arrowLen;
            points.push(`${tipX},${visualDepth}`);
            if (i % 5 === 0 && arrowLen > 2) {
              arrows.push(
                <line
                  key={i}
                  x1={tipX}
                  y1={visualDepth}
                  x2={x_face}
                  y2={visualDepth}
                  stroke="#4caf50"
                  strokeWidth="1.5"
                  markerEnd="url(#arrowheadGreenYZ)"
                />
              );
            }
          });
          return (
            <g>
              <polyline points={points.join(' ')} fill="none" stroke="#4caf50" strokeWidth="2" strokeDasharray="4 2" />
              {arrows}
              <text
                x={isNegative ? x_face - 10 : x_face + 10}
                y={foundationBottom + 12}
                dominantBaseline="hanging"
                textAnchor={isNegative ? 'end' : 'start'}
                fontSize="10"
                fill="#4caf50"
                fontWeight="bold"
              >
                Passive (Rankine){slabLatObj.mobilisation_factor != null && slabLatObj.mobilisation_factor < 0.999 ? ` m=${slabLatObj.mobilisation_factor}` : ''}
              </text>
            </g>
          );
        })()}

        {/* --- PILES --- */}
        {Object.entries(pilesByY).map(([y_key, piles]) => {
          const y_val = parseFloat(y_key);
          const y_visual = offsetX + (W / 2 + y_val) * sectionScale;
          const totalLoad = piles.reduce((sum, p) => sum + (p.load || 0), 0);
          const pileAngleX = piles[0]?.angle_x || 0; // Assume all piles at same Y have same angles
          const pileAngleY = piles[0]?.angle_y || 0;
          const deltaZ = piles[0]?.delta_z ?? pileLength;
          const tipDepth = cappedPileTipDepth(D_embed + H, deltaZ, rockSurfaceDepth);
          const drawnMemberLength = Math.max(0, tipDepth - (D_embed + H));
          const pileBottomVisual = depthY(tipDepth);

          const isDiagonal = pileAngleX !== 0 || pileAngleY !== 0;
          const projectedLengthY = isDiagonal ? deltaZ * Math.tan(pileAngleY * Math.PI / 180) : 0;
          const projectedY = y_visual + projectedLengthY * 0.3; // Scale for visibility

          return (
            <g key={`yz-${y_key}`}>
              {/* Actual Pile - from foundation bottom to pile tip */}
              {isDiagonal ? (
                // 3D Diagonal pile - draw as angled line based on angle_y (for YZ view)
                <line
                  x1={y_visual}
                  y1={foundationBottom}
                  x2={y_visual + Math.tan(pileAngleY * Math.PI / 180) * (pileBottomVisual - foundationBottom)}
                  y2={pileBottomVisual}
                  stroke="#666"
                  strokeWidth="3"
                  opacity={0.7}
                />
              ) : (
                // Vertical pile - draw as rectangle
                <rect x={y_visual - 10} y={foundationBottom} width={20} height={pileBottomVisual - foundationBottom} fill="#aaa" opacity={0.7} stroke="#666" strokeWidth="1" />
              )}

              {/* Projected vector for diagonal piles */}
              {isDiagonal && (
                <>
                  {/* Projection line on XY plane (Y-direction for YZ view) */}
                  <line
                    x1={y_visual}
                    y1={foundationBottom + 5}
                    x2={projectedY}
                    y2={foundationBottom + 5}
                    stroke="#ff6b35"
                    strokeWidth="2"
                    strokeDasharray="5 3"
                    opacity="0.8"
                    markerEnd="url(#arrowheadOrangeYZ)"
                  />
                  {/* Vertical projection indicator */}
                  <line
                    x1={projectedY}
                    y1={foundationBottom + 5}
                    x2={projectedY}
                    y2={foundationBottom - 5}
                    stroke="#ff6b35"
                    strokeWidth="1"
                    opacity="0.6"
                  />
                </>
              )}

              {originalPileLength === pileLength &&
                D_embed + H + pileLength > fullScaleDepth + 0.02 &&
                pileBottomVisual >= depthY(fullScaleDepth) - 2 && (
                <line
                  x1={y_visual - 15} y1={depthY(fullScaleDepth)}
                  x2={y_visual + 15} y2={depthY(fullScaleDepth)}
                  stroke="#ff6b6b" strokeWidth="1" strokeDasharray="2 2"
                >
                  <title>
                    {fullScaleDepth.toFixed(2)} m b.g.l. (slab bottom + {CRITICAL_DEPTH_BELOW_SLAB_BOTTOM_M} m): denser vertical
                    scale below. Not a soil layer boundary.
                  </title>
                </line>
              )}

              {lateral_pressure_dist.length > 0 && Math.abs(horizontal_load_y) > 0.001 && (() => {
                const maxPressure = Math.max(...lateral_pressure_dist.map((p) => Math.abs(p.pressure_kPa || 0)), 1);
                const pressureScaleLat = 60 / maxPressure;
                const p0 = lateral_pressure_dist[0]?.pressure_kPa;
                const isNegative =
                  p0 !== undefined && p0 !== 0 && Math.abs(p0) > 1e-9
                    ? p0 < 0
                    : horizontal_load_y < 0 || (horizontal_load_y === 0 && horizontal_load_x < 0);
                const pileEdgeX = y_visual + (isNegative ? -10 : 10);
                const { fullStr, arrows, hasLine } = buildPileLateralGraphic({
                  lateral_pressure_dist,
                  pileLength: drawnMemberLength,
                  pileBottomVisual,
                  D_embed,
                  H,
                  depthY,
                  pileEdgeX,
                  isLoadNegative: isNegative,
                  pressureScaleLat,
                  keyPrefix: `yz-${y_key}-`,
                  markerGreenUrl: 'url(#arrowheadGreenYZ)',
                });
                return (
                  <g>
                    {hasLine && (
                      <polyline
                        points={fullStr}
                        fill="none"
                        stroke="#4caf50"
                        strokeWidth="2"
                        strokeDasharray="4 2"
                      >
                        <title>Lateral p–y reaction (shape vs depth along pile)</title>
                      </polyline>
                    )}
                    {arrows}
                  </g>
                );
              })()}

              <line x1={y_visual} y1={foundationBottom - 30} x2={y_visual} y2={foundationBottom} stroke="#f44336" strokeWidth="2" markerEnd="url(#arrowheadRedYZ)" />
              <rect x={y_visual - 35} y={foundationBottom - 50} width={70} height={12} fill="white" stroke="none" opacity={0.8} />
              <text x={y_visual} y={foundationBottom - 40} textAnchor="middle" fontSize="10" fill="#f44336" fontWeight="bold">
                {piles.length === 1 ? `${totalLoad.toFixed(0)}kN` : piles.map(p => `${p.load.toFixed(0)}`).join('|') + 'kN'}
              </text>

              {/* Lateral load labels for piles (YZ View - show Y lateral load) */}
              {(() => {
                const totalLatY = piles.reduce((sum, p) => sum + (p.lateral_y || 0), 0);
                if (Math.abs(totalLatY) < 0.1) return null;
                const isNegative = totalLatY < 0;
                const arrowYStart = isNegative ? y_visual + 40 : y_visual - 40;
                const arrowYEnd = y_visual;
                return (
                  <g>
                    <line x1={arrowYStart} y1={foundationBottom + 20} x2={arrowYEnd} y2={foundationBottom + 20} stroke="#2196f3" strokeWidth="1.5" markerEnd="url(#arrowheadBlueYZ)" />
                    <rect x={y_visual - 35} y={foundationBottom + 25} width={70} height={12} fill="white" stroke="none" opacity={0.8} />
                    <text x={y_visual} y={foundationBottom + 35} textAnchor="middle" fontSize="9" fill="#2196f3" fontWeight="bold">
                      {piles.length === 1 ? `${Math.abs(totalLatY).toFixed(1)}kN` : piles.map(p => `${Math.abs(p.lateral_y).toFixed(1)}`).join('|') + 'kN'}
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        <text x={offsetX + W * sectionScale / 2} y={contentYBottom - 24} textAnchor="middle" fontSize="14" fill="#666" fontWeight="bold">YZ View (Looking along X-axis)</text>
      </svg>
    );
  };

  return (
    <Card>
      <CardHeader title="Soil Pressure Distribution (Detailed Line Loads)" />
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Green dashed lines represent the soil lateral pressure (p-y curves). Blue arrows are bearing pressure. Red arrows are pile loads.
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {renderXZView()}
          {renderYZView()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SoilPressureDiagram;
